import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import copy from 'copy-to-clipboard';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/pro-light-svg-icons/faCopy';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { TypedMemo } from '../../common/types';
import { EmbedSnippetAttribute, embedSnippetCode } from './embedSnippet';
import styles from './EmbedCodeBlock.module.scss';

interface EmbedCodeBlockProps {
  /** The snippet's parts. The block shows them and the button copies them. */
  attributes: EmbedSnippetAttribute[];
}

const COPIED_MS = 1600;

/**
 * The code, in the open.
 *
 * The old modal copied a snippet the artist never saw, so a wrong width or a
 * stale address only showed up on the page it was pasted into. What is drawn
 * here and what lands on the clipboard are built from the same attributes.
 */
const EmbedCodeBlockComponent: React.FC<EmbedCodeBlockProps> = ({ attributes }) => {
  const intl = useIntl();
  const [isCopied, setIsCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current !== null) {
        clearTimeout(timer.current);
      }
    },
    []
  );

  const onCopy = useCallback(() => {
    copy(embedSnippetCode(attributes));
    setIsCopied(true);
    if (timer.current !== null) {
      clearTimeout(timer.current);
    }
    timer.current = setTimeout(() => setIsCopied(false), COPIED_MS);
  }, [attributes]);

  return (
    <div className={styles.block}>
      <pre className={styles.code}>
        <code>
          <span className={styles.tag}>&lt;iframe</span>
          {attributes.map((attribute) => (
            <React.Fragment key={attribute.name}>
              {' '}
              <span className={styles.attr}>{attribute.name}</span>
              <span className={styles.punct}>=</span>
              <span className={styles.string}>{`"${attribute.value}"`}</span>
            </React.Fragment>
          ))}
          <span className={styles.tag}>&gt;&lt;/iframe&gt;</span>
        </code>
      </pre>
      <button type="button" className={clsx(styles.copy, isCopied && styles.copied)} onClick={onCopy}>
        <FontAwesomeIcon icon={isCopied ? faCheck : faCopy} />
        {isCopied
          ? intl.formatMessage({ id: 'app.sml.embed.copied', defaultMessage: 'Copied' })
          : intl.formatMessage({ id: 'app.sml.embed.copy', defaultMessage: 'Copy' })}
      </button>
    </div>
  );
};

export const EmbedCodeBlock = TypedMemo(EmbedCodeBlockComponent);
